import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { advertiserApi } from '../../services/advertiserApi';

/**
 * Advertiser profile page — view + edit companyName / contactEmail.
 * Status transitions are admin-only (the field is read-only here).
 */
export function AdvertiserAccountPage() {
  const queryClient = useQueryClient();
  const accountQuery = useQuery({
    queryKey: ['advertiser', 'account'],
    queryFn: () => advertiserApi.getAccount(),
  });

  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(
    null
  );

  // Seed the editable fields from the loaded account, re-syncing whenever the
  // query data changes. React's "adjust state during render" pattern (tracking
  // the previously-synced value) instead of a setState-in-effect.
  const [syncedAccount, setSyncedAccount] = useState(accountQuery.data);
  if (accountQuery.data && accountQuery.data !== syncedAccount) {
    setSyncedAccount(accountQuery.data);
    setCompanyName(accountQuery.data.account.companyName);
    setContactEmail(accountQuery.data.account.contactEmail);
  }

  const updateMutation = useMutation({
    mutationFn: () =>
      advertiserApi.updateAccount({
        companyName: companyName.trim(),
        contactEmail: contactEmail.trim().toLowerCase(),
      }),
    onSuccess: () => {
      setFeedback({ kind: 'success', text: 'Account updated.' });
      return queryClient.invalidateQueries({ queryKey: ['advertiser', 'account'] });
    },
    onError: (err: Error) => {
      setFeedback({ kind: 'error', text: err.message });
    },
  });

  if (accountQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading account" />
      </Box>
    );
  }
  if (accountQuery.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load account: {(accountQuery.error as Error).message}
      </Alert>
    );
  }

  const account = accountQuery.data!.account;
  const dirty =
    companyName.trim() !== account.companyName ||
    contactEmail.trim().toLowerCase() !== account.contactEmail;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Advertiser account
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Account status
            </Typography>
            <Box>
              <Chip label={account.status} sx={{ mt: 0.5 }} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Status transitions are admin-controlled. Contact support to change it.
            </Typography>
          </Box>

          <TextField
            label="Company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Contact email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            fullWidth
            required
          />

          {feedback ? (
            <Alert severity={feedback.kind} role="alert">
              {feedback.text}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => updateMutation.mutate()}
              disabled={!dirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default AdvertiserAccountPage;
